import { Handle, Position } from "@xyflow/react";
import type { ComponentType, ReactNode } from "react";
import { LockBadge } from "../LockBadge";
import { StaleBadge } from "../StaleBadge";
import type { LineageNode } from "../types";
import { VersionChip } from "../VersionChip";

interface NodeShellProps {
  node: LineageNode;
  icon: ComponentType<{ className?: string }>;
  selected?: boolean;
  showVersionChip?: boolean;
  hideHandles?: boolean;
  footer?: ReactNode;
}

export function NodeShell({
  node,
  icon: Icon,
  selected,
  showVersionChip = true,
  hideHandles,
  footer,
}: NodeShellProps) {
  return (
    <div
      className={`w-[240px] cursor-pointer rounded-lg border bg-card p-3 shadow-sm transition-colors ${
        selected ? "border-accent ring-2 ring-accent" : "border-border hover:border-border-strong"
      }`}
    >
      {!hideHandles && (
        <Handle
          type="target"
          position={Position.Left}
          isConnectable={false}
          style={{ opacity: 0, width: 1, height: 1, pointerEvents: "none" }}
        />
      )}
      <div className="flex items-start justify-between gap-2">
        <div className="flex items-center gap-2">
          <Icon className="h-4 w-4 text-text-muted" />
          <div>
            <div className="text-sm font-semibold text-text-primary">{node.label}</div>
            {node.subLabel && <div className="text-xs text-text-muted">{node.subLabel}</div>}
          </div>
        </div>
        {showVersionChip && <VersionChip versionNumber={node.currentVersionNumber} />}
      </div>
      {(node.locked || node.stale || node.generationState) && (
        <div className="mt-2 flex flex-wrap gap-1">
          {node.locked && <LockBadge />}
          {node.stale && <StaleBadge />}
          {node.generationState &&
            node.generationState !== "draft" &&
            node.generationState !== "ready" && (
              <span className="rounded-full bg-blue-50 px-2 py-0.5 text-xs font-medium text-blue-700">
                {node.generationState}
              </span>
            )}
        </div>
      )}
      {footer && <div className="mt-2 text-xs text-text-muted">{footer}</div>}
      {!hideHandles && (
        <Handle
          type="source"
          position={Position.Right}
          isConnectable={false}
          style={{ opacity: 0, width: 1, height: 1, pointerEvents: "none" }}
        />
      )}
    </div>
  );
}
